import { session } from "./pyratSession";

function capitalize_first_character(text: string): string {
    return text.charAt(0).toUpperCase() + (text.slice(1) || "");
}

type TranslationTemplateResolverArguments = { cap?: boolean; plural?: boolean; trans?: boolean };

const translationTemplateDelimiter = "@";

export const TranslationTemplates: { [key: string]: (args: TranslationTemplateResolverArguments) => string } = {
    license: ({ cap = false, plural = false, trans = false }): string => {
        let text: string = session.pyratConf?.LICENCE_NAME?.toLowerCase() || "license";

        if (plural) {
            text = session.pyratConf?.LICENCE_NAME_PLURAL?.toLowerCase() || "licenses";
        }

        if (trans) {
            if (text === "license") {
                text = translated("license");
            } else if (text === "licenses") {
                text = translated("licenses");
            } else if (text === "protocol") {
                text = translated("protocol");
            } else if (text === "protocols") {
                text = translated("protocols");
            } else if (text === "ethical permit") {
                text = translated("ethical permit");
            } else if (text === "ethical permits") {
                text = translated("ethical permits");
            }
        }

        if (cap) {
            text = capitalize_first_character(text);
        }

        return text;
    },

    severity_level: ({ cap = false, plural = false }): string => {
        let text: string = session.pyratConf?.SEVERITY_LEVEL_NAME?.toLowerCase() || "severity_level";

        if (plural) {
            if (text === "pain category") {
                text = "pain categories";
            } else {
                text = "severity levels";
            }
        }

        if (cap) {
            text = capitalize_first_character(text);
        }

        return text;
    },

    cage_project: ({ cap = false, plural = true }): string => {
        let name: string = session.pyratConf?.CAGE_PROJECT_NAME?.toLowerCase() || "cage_project";

        if (name === "billing project") {
            if (plural) {
                name = translated("billing projects");
            } else {
                name = translated("billing project");
            }
        } else if (name === "cage project") {
            if (plural) {
                name = translated("cage projects");
            } else {
                name = translated("cage project");
            }
        } else {
            if (plural) {
                name = translated("projects");
            } else {
                name = translated("project");
            }
        }

        if (cap) {
            name = capitalize_first_character(name);
        }

        return name;
    },

    certificate: ({ cap = false, plural = true }): string => {
        let name: string = session.pyratConf?.CERTIFICATE_NAME || "certificate";

        if (name === "certificate") {
            if (plural) {
                name = translated("certificates");
            } else {
                name = translated("certificate");
            }
        } else if (name === "training record") {
            if (plural) {
                name = translated("training records");
            } else {
                name = translated("training record");
            }
        } else {
            throw new Error("Unknown certificate name configuration: " + name);
        }

        if (cap) {
            name = capitalize_first_character(name);
        }

        return name;
    },
};

/** Parse and apply variables in translated strings. **/
function apply_templates(text: string, delimiter: string = translationTemplateDelimiter): string {
    // check first if there is something to parse
    if (text.indexOf(delimiter) > -1) {
        for (const [key, fn] of Object.entries(TranslationTemplates)) {
            const pattern = new RegExp(`${delimiter}(${key})(|([a-z|]*))?${delimiter}`, "g");
            for (const match of text.matchAll(pattern)) {
                if (match) {
                    const options: TranslationTemplateResolverArguments = {
                        cap: false,
                        plural: false,
                        trans: false,
                    };
                    if (match[3] !== undefined) {
                        for (const m of match[3].split("|")) {
                            if (m in options) {
                                options[m as keyof TranslationTemplateResolverArguments] = true;
                            }
                        }
                    }
                    // biome-ignore lint/style/noParameterAssign:
                    text = text.replace(match[0], fn(options));
                }
            }
        }
    }

    return text;
}

export const translated = (text: string): string => {
    const pyratTranslation = window.top?.pyratTranslation;
    return apply_templates(pyratTranslation?.[text as keyof typeof pyratTranslation] || text);
};

/**
 * Try to parse the given string to a decimal number.
 *
 * @param str: The value to parse.
 * @returns: A floating point number parsed from the given string.
 *           Return NaN when the string cannot be converted to a number.
 */
export function parseDecimal(str: string): number {
    return parseFloat(str.replace(session.localesConf.decimalSymbol, ".").replace(" ", ""));
}

/**
 * Format the given number as a string.
 *
 * @param num: The value to format.
 * @param precision: If precision is given, round d accordingly (0.5->1), before formatting.
 * @param maxPrecision: If max_precision is given, return up to max precision digits without trailing zeros.
 * @returns: A string representing the number.
 *           Return undefined when n is not a number type.
 */
export function formatDecimal(num: number, precision?: number, maxPrecision?: number): string | undefined {
    let result: number | string;
    if (precision) {
        result = Number(Math.round(parseFloat(num + "e" + precision)) + "e-" + precision);
    } else if (maxPrecision) {
        result = Number(Math.round(parseFloat(num + "e" + maxPrecision)) + "e-" + maxPrecision);
        result = result.toFixed(maxPrecision).replace(new RegExp("[.0]{0," + (maxPrecision + 1) + "}$"), "");
    }

    return result.toString().replace(".", session.localesConf.decimalSymbol);
}
